import { IPageAgeGateConfiguration } from '../../domain/conf/page'
import { Utils } from '../../utils/common'
import { DOMEvents } from '../../utils/dom'
import { SimpleEvents } from '../../utils/events'
import { PageAgeGateEvents } from './event'

export class PageAgeGateService extends SimpleEvents {
    public static DOM = {
        className: {
            ageVerificationFailed: 'failed',
            invalidDateInput: 'invalid',
            pageAgeGate: 'age-gate',
            pageGateBodyEnable: 'show-age-gate',
            pageGateForm: 'pn-age-gate',
        },
        ids: {
            inputDay: 'age-gate-d',
            inputMonth: 'age-gate-m',
            inputRemember: 'age-gate-remember-me',
            inputYear: 'age-gate-y',
        },
        localStorage: {
            nextDisplay: 'page-age-get-next-shown-timestamp',
            verifiedKey: 'page-age-gate-verified',
        },
        urlParams: {
            ageGatePassthru: 'utm_agegate',
        }
    }
    public static focusableElements =
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

    public static requiredAge = 19
    public static minYearInput = 1900

    // if user does not want to be 'remembered', do
    // not show them the age gate again for 24 hours, if
    // they do want to be remembered, do not show them the
    // age gate again for 1 year (uses milliseconds unit)
    public static defaultShowIntervalMs = 86400000
    public static rememberMeShowIntervalMs = 31536000000

    /**
     * Promise for awaiting AgeGate verification.
     */
    public static verified: Promise<void> = new Promise<void>((resolve) => {
        document.addEventListener(PageAgeGateEvents.VERIFIED, () => resolve())
    })

    public ageVerificationRequired = false
    public ageGateDelay = 5000
    public kruxPixelId: string = undefined

    private nextDisplayTs: string = undefined

    public getAgeGateElement(): Element | undefined {
        return document.querySelector(`.${PageAgeGateService.DOM.className.pageAgeGate}`)
    }

    public getAgeGateFormElement(): Element | undefined {
        return document.querySelector(`.${PageAgeGateService.DOM.className.pageGateForm}`)
    }

    public showAgeGate(): void {
        document.dispatchEvent(Utils.event(PageAgeGateEvents.VERIFYING))

        const ageGateForm = this.getAgeGateFormElement()
        ageGateForm.addEventListener('submit', this.ageGateVerify.bind(this), false)

        // window.scrollTo(0, 0)
        const docBody = document.querySelector('body')
        docBody.classList.add(PageAgeGateService.DOM.className.pageGateBodyEnable)

        const ageGate = this.getAgeGateElement()

        // get first element to be focused inside modal
        const firstFocusableElement: any = ageGate.querySelectorAll(PageAgeGateService.focusableElements)[0]
        const focusableContent: any = ageGate.querySelectorAll(PageAgeGateService.focusableElements)
        // get last element to be focused inside modal
        const lastFocusableElement: any = focusableContent[focusableContent.length - 1]

        ageGate.addEventListener('keydown', (e: any) => {
            const isTabPressed = e.key === 'Tab'

            if (!isTabPressed) {
                return
            }

            if (e.shiftKey) { // if shift key pressed for shift + tab combination
                if (document.activeElement === firstFocusableElement) {
                    lastFocusableElement.focus() // add focus for the last focusable element
                    e.preventDefault()
                }
            } else { // if tab key is pressed
                // if focused has reached to last focusable element then focus first focusable element
                if (document.activeElement === lastFocusableElement) {
                    firstFocusableElement.focus() // add focus for the first focusable element
                    e.preventDefault()
                }
            }
        })

        firstFocusableElement.focus()
    }

    public hideAgeGate(): void {
        const docBody = document.querySelector('body')
        docBody.classList.remove(PageAgeGateService.DOM.className.pageGateBodyEnable)
    }

    public markFailedAgeGate(): void {
        this.getAgeGateElement().classList.add(PageAgeGateService.DOM.className.ageVerificationFailed)
    }

    public markInvalidDate(): void {
        this.getAgeGateElement().classList.add(PageAgeGateService.DOM.className.invalidDateInput)
    }

    public resetAgeGate(): void {
        this.getAgeGateElement().classList.remove(PageAgeGateService.DOM.className.ageVerificationFailed)
        this.getAgeGateElement().classList.remove(PageAgeGateService.DOM.className.invalidDateInput)
    }

    /**
     * Stores verified state and registers listener(s).
     */
    public initialize(conf: IPageAgeGateConfiguration, isAgeOfMajority = false) {
        const self = this

        if (!conf) {
            return
        }

        self.ageVerificationRequired = conf.enabled
        self.ageGateDelay = conf.delayMs
        self.kruxPixelId = conf.kruxPixelId

        const urlPassThrough = (
            window.location.href.indexOf(
                `${PageAgeGateService.DOM.urlParams.ageGatePassthru}=true`
            ) > -1)

        let verifiedValue: string | undefined = window.localStorage.getItem(
            PageAgeGateService.DOM.localStorage.verifiedKey)

        // start of legacy age-gate check
        // TODO: Remove by December 31st, 2020
        // This method checks for old (legacy) TGO age gate cookie,
        // and migrates to new age-gate cookie format
        if (!verifiedValue) {
            verifiedValue = self.convertLegacyCookie() ? 'true' : undefined
        }
        // end of legacy age-gate check

        self.nextDisplayTs = window.localStorage.getItem(PageAgeGateService.DOM.localStorage.nextDisplay)

        // determines if the current time is past the set time to next display age gate
        // (if user has passed through it before)
        const pastTimeout = (!self.nextDisplayTs || Date.now() > parseInt(self.nextDisplayTs, 10))

        if (self.ageVerificationRequired) {
            if (urlPassThrough || isAgeOfMajority || (verifiedValue && !pastTimeout)) {
                self.onAgeVerified()
            } else {
                setTimeout(() => {
                    self.showAgeGate()
                }, self.ageGateDelay)
            }
        }
    }

    // start of legacy age-gate check
    // TODO: Remove by December 31st, 2020
    // This method checks for old (legacy) TGO age gate cookie,
    // and migrates to new age-gate cookie format
    // end of legacy age-gate check
    public convertLegacyCookie(): boolean {
        const re = new RegExp(/(age_gate=)(\d+)/)
        const matches = re.exec(document.cookie)

        // tslint:disable-next-line: no-magic-numbers
        const age: number | undefined = (matches && matches.length > 2) ? parseInt(matches[2], 10) : undefined

        if (age && age >= PageAgeGateService.requiredAge) {
            this.onAgeVerified()
            this.setNextAgeGateDiplayTime(true)
            return true
        }

        return false
    }

    public ageGateVerify(e: Event) {
        e.preventDefault()

        this.resetAgeGate()

        const today = new Date()
        const maxYear = today.getFullYear() - PageAgeGateService.requiredAge
        const maxDate = today.getDate()
        const maxMonth = today.getMonth()
        const maxBirthDate = new Date(maxYear, maxMonth, maxDate)

        const ageInputMonth = parseInt(document.querySelector<HTMLInputElement>
            (`#${PageAgeGateService.DOM.ids.inputMonth}`).value, 10)
        const ageInputDay = parseInt(document.querySelector<HTMLInputElement>
            (`#${PageAgeGateService.DOM.ids.inputDay}`).value, 10)
        const ageInputYear = parseInt(document.querySelector<HTMLInputElement>
            (`#${PageAgeGateService.DOM.ids.inputYear}`).value, 10)

        if (!this.dateIsValid(ageInputYear, ageInputMonth, ageInputDay)) {
            return this.markInvalidDate()
        }

        // subtract 1 from ageInputMonth, as it is given as an index starting at
        // 0 for December, to 11 for December
        const ageInputDate = new Date(ageInputYear, ageInputMonth - 1, ageInputDay)

        const rememberMe = (document.querySelector(
            `#${PageAgeGateService.DOM.ids.inputRemember}:checked`
        ) !== null)

        if (ageInputDate <= maxBirthDate && ageInputYear >= PageAgeGateService.minYearInput) {
            this.hideAgeGate()

            this.onAgeVerified()
            this.setNextAgeGateDiplayTime(rememberMe)
        } else {
            this.markFailedAgeGate()
        }

        return false
    }

    public dateIsValid(year: number, month: number, day: number) {
        if (isNaN(year) || isNaN(month) || isNaN(day)) {
            return false
        }

        const d = new Date([year, month, day].join('/'))

        if (!isNaN(d.getTime())) {
            // date is valid
            return year >= PageAgeGateService.minYearInput
        }

        return false
    }

    /**
     * This method sets the next time that the user should see the age gate based on
     * whether they want to be rememebered or not, in window localstorage for future lookup
     * @param rememberMe Whether or not user wants to be remembered
     */
    public setNextAgeGateDiplayTime(rememberMe: boolean) {
        let nextTs = Date.now()
        if (rememberMe) {
            nextTs += PageAgeGateService.rememberMeShowIntervalMs
        } else {
            nextTs += PageAgeGateService.defaultShowIntervalMs
        }

        this.nextDisplayTs = nextTs.toString()

        localStorage.setItem(PageAgeGateService.DOM.localStorage.nextDisplay, this.nextDisplayTs)
    }

    /**
     * Applies messaging and fires 'age verified' event.
     */
    public onAgeVerified() {
        localStorage.setItem(PageAgeGateService.DOM.localStorage.verifiedKey, 'true')
        document.dispatchEvent(Utils.event(PageAgeGateEvents.VERIFIED))

        if (!this.nextDisplayTs) {
            this.setNextAgeGateDiplayTime(false)
        }

        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
            detail: {
                control_fields: {
                    mparticle: {
                        mp_event_type: 'UserContent',
                    }
                },
                event_name: 'age_gate_passed'
            }
        }))

        if (this.kruxPixelId) {
            document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                detail: {
                    control_fields: {
                        krux: {
                            krx_event_id: this.kruxPixelId,
                            krx_event_type: 'default'
                        }
                    },
                    event_name: 'Age Gate'
                }
            }))
        }
    }

}
